import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTourForPath, welcomeTour } from "../common/tourContent";
import { hasSeenTour, markTourSeen, areToursDisabled, setToursDisabled } from "../common/tourState";

// Decides which tour, if any, should be on screen.
//
// Auto-start rules: the welcome tour runs once on first login, and each page's
// tour runs once the first time that page is opened. After that they're opt-in
// from the help panel. `startPageTour` ignores both rules so the help button
// can always replay the current page's tour.
export function useGuidedTour() {
  const location = useLocation();
  const [activeTour, setActiveTour] = useState(null);
  const pageTour = getTourForPath(location.pathname);

  useEffect(() => {
    if (activeTour || areToursDisabled()) return;

    // Wait a beat so the route's own data has had a chance to render —
    // otherwise steps targeting a table or chart get filtered out as missing.
    const timer = setTimeout(() => {
      if (!hasSeenTour(welcomeTour.id)) {
        setActiveTour(welcomeTour);
        return;
      }
      if (pageTour && !hasSeenTour(pageTour.id)) {
        setActiveTour(pageTour);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [location.pathname, pageTour, activeTour]);

  const finishTour = useCallback(() => {
    setActiveTour((current) => {
      if (current) markTourSeen(current.id);
      return null;
    });
  }, []);

  const disableAllTours = useCallback(() => {
    setToursDisabled(true);
    finishTour();
  }, [finishTour]);

  // Replay the current page's tour on demand, seen or not.
  const startPageTour = useCallback(() => {
    if (pageTour) setActiveTour(pageTour);
  }, [pageTour]);

  const startWelcomeTour = useCallback(() => setActiveTour(welcomeTour), []);

  return {
    activeTour,
    hasPageTour: Boolean(pageTour),
    pageTourTitle: pageTour?.title,
    startPageTour,
    startWelcomeTour,
    finishTour,
    disableAllTours,
  };
}
